var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useContext, useReducer, useMemo } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { Transition } from 'react-transition-group';
import uuid from 'uuid';
import { handleEnter } from '..';
var transitionDuration = 200;
var transitionStyles = {
    entering: { opacity: 1 },
    entered: { opacity: 1 },
    exiting: { opacity: 0 },
    exited: { display: 'none', opacity: 0 },
};
var toastContext = React.createContext(null);
var initialState = { toasts: [] };
function reducer(state, action) {
    switch (action.type) {
        case 'dismissToast':
            return {
                toasts: state.toasts.map(function (toast) {
                    return toast.visible && toast.id === action.payload ? __assign(__assign({}, toast), { visible: false }) : toast;
                }),
            };
        case 'addToast':
            return { toasts: state.toasts.concat(action.payload) };
        default:
            throw new Error("Unknown action type " + action.type);
    }
}
function ToastRenderer(_a) {
    var toasts = _a.toasts, dismissToast = _a.dismissToast, portalSelector = _a.portalSelector;
    var content = (React.createElement(React.Fragment, null, toasts.map(function (_a) {
        var id = _a.id, visible = _a.visible, _b = _a.config, dismissable = _b.dismissable, variation = _b.variation, onClick = _b.onClick, onDismiss = _b.onDismiss, content = _b.content;
        return (React.createElement(Transition, { in: visible, timeout: transitionDuration, appear: true, key: id }, function (state) {
            var handleDismiss = function () {
                onDismiss && onDismiss();
                dismissToast(id);
            };
            return (React.createElement("div", { className: classNames('toast-item', variation), style: __assign({ transition: "opacity " + transitionDuration + "ms ease" }, transitionStyles[state]), role: "button", tabIndex: 0, onKeyPress: handleEnter(onClick), onClick: onClick },
                React.createElement("span", { className: "toast-item-content" }, content),
                dismissable ? (React.createElement("i", { className: "icon-cross close-toast", role: "button", tabIndex: 0, onKeyPress: handleEnter(handleDismiss), onClick: handleDismiss })) : null));
        }));
    })));
    if (portalSelector) {
        var container = document.querySelector(portalSelector);
        if (!container) {
            throw new Error("Missing " + portalSelector);
        }
        return ReactDOM.createPortal(content, container);
    }
    return content;
}
export function useToast() {
    return useContext(toastContext);
}
export function WithToasts(_a) {
    var children = _a.children, portalSelector = _a.portalSelector;
    var _b = useReducer(reducer, initialState), state = _b[0], dispatch = _b[1];
    var contextValue = useMemo(function () { return ({
        addToast: function (config) {
            var id = uuid.v4();
            var timeout;
            if (typeof config.duration === 'number') {
                timeout = setTimeout(function () {
                    config.onDismiss && config.onDismiss();
                    dispatch({ type: 'dismissToast', payload: id });
                }, config.duration);
            }
            dispatch({ type: 'addToast', payload: { id: id, config: config, visible: true, timeout: timeout } });
        },
    }); }, [dispatch]);
    var dismissToast = useCallback(function (id) {
        dispatch({ type: 'dismissToast', payload: id });
    }, [dispatch]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ToastRenderer, { portalSelector: portalSelector === undefined ? '#toasts-container' : portalSelector, toasts: state.toasts, dismissToast: dismissToast }),
        React.createElement(toastContext.Provider, { value: contextValue }, children)));
}
export function withToasts(conf) {
    if (conf === void 0) { conf = {}; }
    return function withToastHoc(Component) {
        function Wrapper(props) {
            return (React.createElement(WithToasts, __assign({}, conf),
                React.createElement(Component, __assign({}, props))));
        }
        Wrapper.displayName = (Component.displayName || Component.name || 'Widget') + "WithToasts";
        return Wrapper;
    };
}
//# sourceMappingURL=Toast.js.map